import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import { useC360List, C360Table, ColumnChooser, EditIcon, DeleteIcon, unwrapLookupItems } from './c360';
import { relationLabel } from './relationConfigs';
import { usePageMeta } from '../hooks/usePageMeta';
import { DOC_TYPES, TARGETS, PAPER_SIZES } from './belgeDizayni/fieldCatalog';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/documenttemplates';

const docTypeLabel = (v) => (DOC_TYPES.find((d) => d.value === Number(v)) || {}).label || '-';
const targetLabel = (v) => (TARGETS.find((t) => t.value === Number(v)) || {}).label || '-';
const docTypeTone = (v) => (Number(v) === 2 ? 'ok' : Number(v) === 1 ? 'neutral' : 'wait');

const COLUMN_FILTER_META = {
  name: { type: 'text', odataField: 'Name' },
  docType: { type: 'select', odataField: 'DocType', options: DOC_TYPES },
  target: { type: 'select', odataField: 'Target', options: TARGETS.map((t) => ({ value: t.value, label: t.label })) },
};
const SEARCH_FIELDS = ['Name'];

function displayDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleString('tr-TR');
}

export function BelgeDizayniPage() {
  usePageMeta('Belge Dizaynı', 'Sipariş, İrsaliye ve Fatura belgeleri için yazdırılabilir düzenler tasarlayın.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  // Talep 834 — Distribütör kolonu. Liste ham entity döndürür (hazır ad yok) → id→etiket
  // eşlemesi istemcide, tek lookup isteğiyle çözülür (diğer c360 listelerindeki desen).
  const [vendorLabels, setVendorLabels] = useState({});

  useEffect(() => {
    let alive = true;
    fetch('/api/vendors/Select', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : []))
      .then((j) => {
        if (!alive) return;
        const map = {};
        unwrapLookupItems(j).forEach((v) => { map[String(v.id ?? v.Id)] = relationLabel(v); });
        setVendorLabels(map);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'UpdatedDate',
    defaultSortDir: 'desc',
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData,
  } = list;

  const openDesigner = (row) => navigate(`/belge-dizayni/${row.id}`);

  const handleDelete = async () => {
    if (!selected) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: selected.id }),
      });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Dizayn silindi');
      setDeleteConfirm(false);
      setSelected(null);
      fetchData();
    } catch (e) {
      showToast('error', e.message || 'Silme sırasında hata oluştu');
      setDeleteConfirm(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('name', {
      header: 'Dizayn Adı', meta: { filterType: 'text' },
      cell: (info) => info.getValue() || '-',
    }),
    columnHelper.accessor('docType', {
      header: 'Tür', meta: { filterType: 'select' },
      cell: (info) => <StatusBadge tone={docTypeTone(info.getValue())}>{docTypeLabel(info.getValue())}</StatusBadge>,
    }),
    columnHelper.accessor('vendorId', {
      header: 'Distribütör',
      // Boş = tüm distribütörler için geçerli genel dizayn — "-" göstermek bunu "tanımsız"
      // gibi okuturdu.
      cell: (info) => {
        const v = info.getValue();
        if (!v) return <span style={{ color: 'var(--text-secondary)' }}>Tüm distribütörler</span>;
        return vendorLabels[String(v)] || '…';
      },
    }),
    columnHelper.accessor('target', {
      header: 'Hedef', meta: { filterType: 'select' },
      cell: (info) => targetLabel(info.getValue()),
    }),
    columnHelper.accessor('paperSize', {
      header: 'Kağıt', meta: { filterType: 'text' },
      cell: (info) => (PAPER_SIZES[info.getValue()]?.label) || info.getValue() || '-',
    }),
    columnHelper.accessor('isDefault', {
      header: 'Varsayılan',
      cell: (info) => (info.getValue() ? <StatusBadge tone="ok">Varsayılan</StatusBadge> : '-'),
    }),
    columnHelper.accessor('updatedDate', {
      header: 'Güncelleme',
      cell: (info) => displayDate(info.getValue()),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Tasarla" aria-label="Tasarla" onClick={(e) => { e.stopPropagation(); openDesigner(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(true); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete, vendorLabels]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('UpdatedDate', 'desc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Dizayn adı ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Belge Dizaynı"
        subtitle="Sipariş, İrsaliye ve Fatura belgeleri için yazdırılabilir düzenler tasarlayın."
        actions={canAdd && <button style={newRecordButtonStyle} onClick={() => navigate('/belge-dizayni/new')}>+ Yeni Belge Dizaynı</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDesigner}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(false)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}>Bu dizaynı silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}><button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button><button style={s.btnSecondary} onClick={() => setDeleteConfirm(false)}>İptal</button></div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}
